import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Mail } from "lucide-react";
import { errorMessage, getCompanyEmail, type CompanyEmail } from "@/api/email";
import { Skeleton } from "@/components/ui/skeleton";
import { CampaignContactSheet } from "@/components/email/CampaignContactSheet";
import { ContactStatusBadge, DryRunNotice, TonePill } from "@/components/email/EmailBadges";
import { StepProgress } from "@/components/email/StepProgress";
import { useEmailMeta } from "@/hooks/useEmailMeta";
import { COMPANY_STATUS, EVENT_LABEL, LEGACY_ENROLLED, shortAgo } from "@/lib/email";
import { campaignsPath } from "@/lib/emailRoutes";
import { formatDateTime } from "@/lib/formatters";

/** The company's people in Email module campaigns: campaign, contact, email
 *  status, current step, last event — each opening its full conversation.
 *  Distinct from the legacy "Email sequence" panel, which it never mixes with:
 *  a company is emailed one way only. */
export function CompanyEmailCampaigns({
  companyId,
  projectSlug,
}: {
  companyId: number;
  projectSlug: string;
}) {
  const { meta } = useEmailMeta(projectSlug);
  const [email, setEmail] = useState<CompanyEmail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    getCompanyEmail(projectSlug, companyId)
      .then((data) => !cancelled && setEmail(data))
      .catch((err) => !cancelled && setError(errorMessage(err, "Email campaigns could not be loaded.")));
    return () => {
      cancelled = true;
    };
  }, [projectSlug, companyId]);

  useEffect(() => load(), [load]);

  const memberships = email?.memberships ?? [];
  return (
    <section
      id="company-email-campaigns"
      className="rounded-plane bg-white p-5 shadow-plane"
      data-testid="company-email-campaigns"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-slate-900">Email campaigns</h3>
            {email?.email_status && <TonePill tone={COMPANY_STATUS[email.email_status]} />}
          </div>
          <p className="mt-0.5 text-[11px] text-slate-400">Campaigns and conversations</p>
        </div>
        <Link
          to={campaignsPath(projectSlug)}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700"
        >
          Open Email <ArrowUpRight className="h-3 w-3" />
        </Link>
      </header>

      {meta && !meta.external_effects && (
        <div className="mt-3"><DryRunNotice compact /></div>
      )}

      {email?.legacy_enrolled && (
        <p className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs leading-relaxed text-amber-900">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <span className="font-semibold">{LEGACY_ENROLLED}.</span> This company is in a Lemlist sequence of
            the legacy workflow (the Email sequence panel): none of its contacts can join a campaign here until
            it is removed there.
          </span>
        </p>
      )}

      <div className="mt-3">
        {error ? (
          <p className="text-sm text-rose-600">{error}</p>
        ) : !email ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : memberships.length === 0 ? (
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 px-4 py-4 text-sm text-slate-500">
            <Mail className="h-4 w-4 text-slate-300" />
            No contact of this company is in an Email campaign.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100">
            {memberships.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => setOpen(m.id)}
                  className="w-full space-y-1.5 px-4 py-3 text-left transition-colors hover:bg-indigo-50/40"
                  aria-label={`${m.contact_name} in ${m.campaign_name}`}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="min-w-0 truncate text-sm font-medium text-slate-900">{m.contact_name}</span>
                    <ContactStatusBadge status={m.status} reason={m.status_reason} />
                  </span>
                  <span className="flex items-center justify-between gap-3 text-xs text-slate-500">
                    <span className="min-w-0 truncate">{m.campaign_name}</span>
                    <span className="flex shrink-0 items-center gap-3">
                      <StepProgress current={m.current_step} total={m.steps_count} />
                      {m.last_event_type ? (
                        <span className="whitespace-nowrap" title={formatDateTime(m.last_event_at)}>
                          {EVENT_LABEL[m.last_event_type] ?? m.last_event_type} · {shortAgo(m.last_event_at)}
                        </span>
                      ) : (
                        <span className="whitespace-nowrap text-slate-400">No event yet</span>
                      )}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {meta && (
        <CampaignContactSheet
          slug={projectSlug}
          membershipId={open}
          meta={meta}
          onOpenChange={(value) => {
            if (!value) {
              setOpen(null);
              load();
            }
          }}
        />
      )}
    </section>
  );
}
